import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Importar useNavigate
import { Container, Typography, Box, Table, TableBody, TableCell, TableHead, TableRow, Paper, TableContainer, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, TablePagination } from '@mui/material';
import Navigation from '../../../Layouts/Navigation/Navigation';
import axios from 'axios';
import { BASE_URL } from '../../../utils/constants';
import { format } from 'date-fns'; // Importar la función format de date-fns

const TowerVisit = () => {
  const { id_torre } = useParams(); // Obtener el ID de la torre desde la URL
  const navigate = useNavigate(); // Inicializar useNavigate
  const [visits, setVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]); // Estado para las visitas filtradas
  const [searchTerm, setSearchTerm] = useState(''); // Estado para el término de búsqueda
  const [selectedVisit, setSelectedVisit] = useState(null); // Estado para la visita seleccionada
  const [openDialog, setOpenDialog] = useState(false); // Estado para controlar el modal

  // Estados para la paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchVisits = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/visits/${id_torre}`);
        setVisits(response.data);
        setFilteredVisits(response.data); // Inicialmente, las visitas filtradas son todas las visitas
      } catch (error) {
        console.error('Error fetching visits:', error);
      }
    };

    fetchVisits();
  }, [id_torre]);

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    // Filtrar las visitas según el término de búsqueda
    const filtered = visits.filter((visit) =>
      visit.nombre.toLowerCase().includes(value) ||
      visit.apellido.toLowerCase().includes(value) ||
      visit.fecha.toLowerCase().includes(value) ||
      visit.placa.toLowerCase().includes(value)
    );
    setFilteredVisits(filtered);
    setPage(0); // Reiniciar la página al realizar una búsqueda
  };

  const handleOpenDialog = (visit) => {
    setSelectedVisit(visit);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedVisit(null);
    setOpenDialog(false);
  };

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    return format(new Date(dateString), 'yyyy-MM-dd hh:mm:ss a'); // Formato con AM/PM
  };

  const getRowColor = (flag) => {
    switch (flag) {
      case 2:
        return '#ffdddd'; // Rojo para flag 2
      case 1:
        return '#ddffdd'; // Verde para flag 1
      case 0:
        return '#fff4ec'; // Naranja para flag 0
      default:
        return 'inherit';
    }
  };

  // Manejar el cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Manejar el cambio de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reiniciar a la primera página
  };

  return (
    <>
      <Navigation />
      <Container sx={{ marginBottom: '50px' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
          Reporte de Visitas - Torre {id_torre}
        </Typography>

        {/* Botón de Volver */}
        <Box sx={{ marginBottom: '20px', textAlign: 'left' }}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Volver
          </Button>
        </Box>

        {/* Buscador */}
        <Box sx={{ marginBottom: '20px' }}>
          <TextField
            label="Buscar visita"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Buscar por nombre, apellido, fecha o placa"
          />
        </Box>

        <Box component={Paper} sx={{ padding: '20px' }}>
          {filteredVisits.length > 0 ? (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Torre</TableCell>
                      <TableCell>Apartamento</TableCell>
                      <TableCell>Visitante</TableCell>
                      <TableCell>Acompañantes</TableCell>
                      <TableCell>Vehículo</TableCell>
                      <TableCell>Placa</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredVisits
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((visit) => (
                        <TableRow
                          key={visit.id_visita}
                          sx={{ backgroundColor: getRowColor(visit.flag_ingreso) }}
                        >
                          <TableCell>{formatDate(visit.fecha)}</TableCell>
                          <TableCell>{visit.torre_nombre}</TableCell>
                          <TableCell>{visit.apto_num_apartamento}</TableCell>
                          <TableCell>{visit.nombre} {visit.apellido}</TableCell>
                          <TableCell>{visit.num_acompanantes}</TableCell>
                          <TableCell>{visit.tipo_vehiculo}</TableCell>
                          <TableCell>{visit.placa}</TableCell>
                          <TableCell>
                            {visit.flag_ingreso === 0
                              ? 'Disponible'
                              : visit.flag_ingreso === 1
                              ? 'Ingresado'
                              : 'Expirado'}
                          </TableCell>
                          <TableCell>
                            <Button variant="contained" onClick={() => handleOpenDialog(visit)}>
                              Ver Detalles
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginador */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredVisits.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          ) : (
            <Typography>No hay visitas registradas para esta torre.</Typography>
          )}
        </Box>
      </Container>

      {/* Modal para mostrar detalles de la visita */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>Detalles de la Visita</DialogTitle>
        <DialogContent>
          {selectedVisit ? (
            <>
              <Typography variant="h6">Información de la Visita</Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>Estado de la Visita</TableCell>
                    <TableCell>
                      {selectedVisit.flag_ingreso === 0
                        ? 'Disponible'
                        : selectedVisit.flag_ingreso === 1
                        ? 'Ingresado'
                        : 'Expirado'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>{selectedVisit.nombre} {selectedVisit.apellido}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>{formatDate(selectedVisit.fecha)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Acompañantes</TableCell>
                    <TableCell>{selectedVisit.num_acompanantes}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Vehículo</TableCell>
                    <TableCell>{selectedVisit.tipo_vehiculo}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Placa</TableCell>
                    <TableCell>{selectedVisit.placa}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Observaciones</TableCell>
                    <TableCell>{selectedVisit.observacion}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : (
            <Typography>Cargando...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TowerVisit;
